import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Sparkles, Download, Share2, Copy, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import * as htmlToImage from "html-to-image";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function TestShare() {
  const { shareId } = useParams();
  const [data, setData] = useState(null);
  const cardRef = useRef(null);

  useEffect(() => {
    axios.get(`${BACKEND}/api/share/test/${shareId}`)
      .then(r => setData(r.data))
      .catch(() => setData(false));
  }, [shareId]);

  if (data === null) return <div className="min-h-screen flex items-center justify-center text-[#5C5870]">Memuat…</div>;
  if (data === false) return (
    <div className="min-h-screen flex items-center justify-center flex-col gap-4">
      <p className="text-[#5C5870]">Link tidak valid atau sudah dihapus.</p>
      <Link to="/" className="lumina-btn-primary">Kembali ke HearInsight</Link>
    </div>
  );

  const shareUrl = window.location.href;
  const shareText = `Saya baru saja menyelesaikan "${data.test_title}" di HearInsight — hasilnya: ${data.tier_title}! ✨`;

  const copy = () => { navigator.clipboard.writeText(shareUrl); toast.success("Link tersalin"); };
  const download = async () => {
    if (!cardRef.current) return;
    try {
      const dataUrl = await htmlToImage.toPng(cardRef.current, { pixelRatio: 2, cacheBust: true });
      const link = document.createElement("a");
      link.download = `hearinsight-${data.test_title.replace(/\s+/g, "-").toLowerCase()}.png`;
      link.href = dataUrl;
      link.click();
      toast.success("Card terdownload");
    } catch (e) {
      toast.error("Download gagal — coba screenshot manual");
    }
  };

  return (
    <div className="min-h-screen lumina-bg-soft py-12 px-4" data-testid="test-share-page">
      <div className="max-w-2xl mx-auto">
        <Link to="/" className="inline-flex items-center gap-2 mb-6">
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#6B4BA3] to-[#9D84D2] flex items-center justify-center">
            <span className="text-white font-serif">H</span>
          </div>
          <span className="font-serif text-xl text-[#1A1728]">HearInsight</span>
        </Link>

        <div ref={cardRef} className="lumina-card overflow-hidden mb-6" data-testid="share-card">
          <div className="bg-gradient-to-br from-[#6B4BA3] via-[#7E5FB5] to-[#9D84D2] text-white p-10 relative overflow-hidden">
            <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-white/10 blur-3xl"></div>
            <div className="absolute -bottom-20 -left-20 w-64 h-64 rounded-full bg-white/10 blur-3xl"></div>
            <div className="relative z-10">
              <div className="flex items-center gap-2 mb-6">
                <div className="w-8 h-8 rounded-full bg-white/20 backdrop-blur flex items-center justify-center">
                  <span className="font-serif text-sm">H</span>
                </div>
                <span className="text-xs uppercase tracking-[0.3em] opacity-90">HearInsight • Insight Card</span>
              </div>
              <p className="text-xs uppercase tracking-widest opacity-70 mb-2">Hasil Tes</p>
              <h2 className="font-serif text-2xl mb-1 opacity-90">{data.test_title}</h2>
              <h1 className="font-serif text-5xl mb-4 leading-tight">{data.tier_title}</h1>
              <p className="text-white/90 leading-relaxed mb-6">{data.tier_description}</p>
              <div className="flex items-center justify-between pt-6 border-t border-white/20">
                <div>
                  <div className="text-xs opacity-70 uppercase tracking-wider">Diraih oleh</div>
                  <div className="font-serif text-xl">{data.user_first_name}</div>
                </div>
                <div className="flex items-center gap-2 bg-white/20 backdrop-blur rounded-full px-4 py-2">
                  <Sparkles className="w-4 h-4" /> Skor <span className="font-bold">{data.score}</span>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white p-6 text-center text-sm text-[#5C5870]">
            Tertarik cek dirimu sendiri? Coba tes psikologi di <span className="text-[#6B4BA3] font-semibold">hearinsight.id</span>
          </div>
        </div>

        <div className="flex flex-wrap gap-3 mb-6">
          <button onClick={download} className="lumina-btn-primary" data-testid="download-card-btn"><Download className="w-4 h-4" /> Download PNG</button>
          <a href={`https://wa.me/?text=${encodeURIComponent(shareText + " " + shareUrl)}`} target="_blank" rel="noreferrer" className="lumina-btn-secondary" data-testid="share-wa">
            <Share2 className="w-4 h-4" /> WhatsApp
          </a>
          <a href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noreferrer" className="lumina-btn-secondary" data-testid="share-twitter">
            <Share2 className="w-4 h-4" /> Twitter / X
          </a>
          <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noreferrer" className="lumina-btn-secondary" data-testid="share-linkedin">
            <Share2 className="w-4 h-4" /> LinkedIn
          </a>
          <button onClick={copy} className="lumina-btn-outline" data-testid="copy-link-btn"><Copy className="w-4 h-4" /> Copy Link</button>
        </div>

        <div className="lumina-card p-6 bg-[#E8F0FA]/50">
          <div className="flex items-center gap-2 mb-2 text-[#6B4BA3]"><CheckCircle2 className="w-5 h-5" /> <span className="font-medium">Coba tes lain di HearInsight</span></div>
          <p className="text-sm text-[#5C5870] mb-3">7+ tes psikologi profesional — gratis & premium.</p>
          <Link to="/tests" className="lumina-btn-primary inline-flex">Lihat semua tes</Link>
        </div>
      </div>
    </div>
  );
}
